import { useState, useCallback } from 'react';
import { getPublicProfile, getPublicProfileByUsername } from '../../services/api-client';
import { message } from 'antd';
import i18next from 'i18next';

/**
 * Hook to manage public profile viewing functionality.
 *
 * @returns {Object} - Object containing profile state and methods
 */
const usePublicProfile = () => {
    const [profileVisible, setProfileVisible] = useState(false);
    const [profileData, setProfileData] = useState(null);
    const [profileLoading, setProfileLoading] = useState(false);

    /**
     * Open public profile by user ID
     */
    const openProfileById = useCallback(async (userId) => {
        if (!userId) return;

        setProfileLoading(true);
        try {
            const response = await getPublicProfile(userId);
            if (response.data) {
                setProfileData(response.data);
                setProfileVisible(true);
            }
        } catch (error) {
            console.error('Failed to load public profile:', error);
            message.error(i18next.t('errors.profileLoadFailed'));
        } finally {
            setProfileLoading(false);
        }
    }, []);

    /**
     * Open public profile by username
     */
    const openProfileByUsername = useCallback(async (username) => {
        if (!username) return;

        setProfileLoading(true);
        try {
            const response = await getPublicProfileByUsername(username);
            if (response.data) {
                setProfileData(response.data);
                setProfileVisible(true);
            }
        } catch (error) {
            console.error('Failed to load public profile:', error);
            message.error(i18next.t('errors.profileLoadFailed'));
        } finally {
            setProfileLoading(false);
        }
    }, []);

    /**
     * Open public profile with already loaded data
     */
    const openProfileWithData = useCallback((userData) => {
        if (!userData) return;
        setProfileData(userData);
        setProfileVisible(true);
    }, []);

    /**
     * Close the profile modal
     */
    const closeProfile = useCallback(() => {
        setProfileVisible(false);
        // Keep profileData for a moment to prevent flickering during close animation
        setTimeout(() => {
            setProfileData(null);
        }, 300);
    }, []);

    return {
        profileVisible,
        profileData,
        profileLoading,
        openProfileById,
        openProfileByUsername,
        openProfileWithData,
        closeProfile,
    };
};

export default usePublicProfile;
